'use client'

import React, { useMemo } from 'react';
import ListWithIconsElement from './ListWithIconsElement';

// ========================================================================
// --- Type Definitions for Props (The Core of the Refactor) ---
// ========================================================================

// A generic base type for any item in the list. It MUST have an ID.
// The [key: string]: any allows for other properties like 'name', 'status', etc.
interface ItemWithId {
  id: string | number;
  [key: string]: any;
}

// A strongly-typed definition for a single option in the dropdown menu.
interface ItemOption<T> {
  name: string;
  /** The function to call when this option is clicked. It receives the entire item. */
  onClick: (item: T) => void;
}

// The main Props interface, now using TypeScript generics (<T>).
// This allows the component to work with any type of data (Events, Tickets, Profiles)
// as long as it has an 'id' property.
interface Props<T extends ItemWithId> {
  /** The array of data objects to display. */
  listItems: T[];

  /** The key of the item object to use as the main title. */
  titleKey: keyof T;

  /** A function to be called when the main list item (e.g., the title) is clicked. */
  onItemClick: (item: T) => void;
  
  /** (Optional) An array of actions for the 'three-dot' dropdown menu. */
  itemOptions?: ItemOption<T>[];
  
  /** (Optional) The key of the item object to use as the subtitle. */
  subtitleKey?: keyof T;
  
  /** (Optional) The text label to display before the subtitle value. */
  subtitleName?: string;
  
  /** (Optional) If provided, the list will only show items where `item.status` matches this string. */
  statusFilter?: string;
}


// ========================================================================
// --- The Component ---
// ========================================================================

const ListWithIcons = <T extends ItemWithId>({
  listItems,
  titleKey,
  onItemClick,
  itemOptions,
  subtitleKey,
  subtitleName,
  statusFilter,
}: Props<T>) => {


  // --- Performance Optimization with useMemo ---
  // This filtering logic now only runs when `listItems` or `statusFilter` changes,
  // not on every single re-render.
  const filteredItems = useMemo(() => {
    if (!statusFilter) {
      return listItems || [];
    }
    // Ensure listItems is an array before filtering
    return Array.isArray(listItems) 
      ? listItems.filter((item: any) => item.status === statusFilter) 
      : [];
  }, [listItems, statusFilter]);


  // Handle the case where there are no items to display
  if (!filteredItems || filteredItems.length === 0) {
    return (
      <div className="mt-6 py-10 text-center text-gray-500">
        <p>No items to display.</p>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ul role="list" className="divide-y divide-gray-200">
          {filteredItems.map((item:any, idx:any) => (
            <li key={idx} className="py-2">
              <ListWithIconsElement
                item={item}
                titleKey={titleKey}
                onItemClick={onItemClick}
                itemOptions={item.options || itemOptions}
                subtitleKey={subtitleKey}
                subtitleName={subtitleName}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ListWithIcons;